import { getApiUrl } from "@/utils/api";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Home, Users, Award, Bed, Bath, MapPin, Search, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { usePageContent } from "@/hooks/usePageContent";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { useSEO } from "@/hooks/useSEO";
import { resolveImageUrl, getOptimizedImageUrl, getResponsiveSrcSet } from "@/utils/imageUrl";
import ProgressiveImg from "@/components/ProgressiveImg";
import axios from "axios";

const API = getApiUrl();
const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1757264119016-7e6b568b810d?crop=entropy&cs=srgb&fm=jpg&q=85";

const formatPrice = (price, currency = "£") => {
  if (!price && price !== 0) return "Price on Application";
  try {
    return `${currency}${Number(price).toLocaleString()}`;
  } catch {
    return `${currency}${price}`;
  }
};

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seoData, setSeoData] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [stylesReady, setStylesReady] = useState(false);
  const { get } = usePageContent("home");
  const { styles } = useSiteStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const propertyLinkState = { background: location };

  // Resolve hero image: CMS uploaded image > default
  const heroImageUrl = styles.heroImage ? resolveImageUrl(styles.heroImage) : DEFAULT_HERO_IMAGE;

  // Hero is ready when both image is loaded and styles are applied
  const heroReady = imageLoaded && stylesReady;

  const cardStyle = {
    backgroundColor: styles.cardBackground || "#e8f0e8",
    borderColor: styles.cardBorder || "#c5d9c5"
  };

  // Mark styles as ready once they've loaded from API
  useEffect(() => {
    // Check if styles object has any custom values (not just defaults)
    if (styles && Object.keys(styles).length > 5) {
      setStylesReady(true);
    }
  }, [styles]);

  useEffect(() => {
    fetchProperties();
    fetchSeoData();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      // Only show featured properties on home page
      const featuredProperties = response.data.filter(p => p.featured === true);
      setProperties(featuredProperties);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoData = async () => {
    try {
      const response = await axios.get(`${API}/seo/home`);
      setSeoData(response.data);
    } catch (error) {
      console.error("Error fetching SEO data:", error);
    }
  };

  // Apply SEO tags via direct DOM manipulation (more reliable than react-helmet-async with React 19)
  useSEO(
    seoData ? {
      seo_title: seoData.seo_title || "SellLocalHomes - Find Your Dream Home",
      seo_description: seoData.seo_description || "Discover exceptional properties in prime locations. Your perfect home is waiting.",
      seo_keywords: seoData.seo_keywords || "property for sale, homes for sale, UK property, real estate",
      seo_robots: seoData.seo_robots || "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/"
    } : {
      seo_title: "SellLocalHomes - Find Your Dream Home",
      seo_description: "Discover exceptional properties in prime locations. Your perfect home is waiting.",
      seo_keywords: "property for sale, homes for sale, UK property, real estate",
      seo_robots: "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/"
    },
    { og_type: "website", og_image: heroImageUrl }
  );

  // Tetris layout pattern
  const getSpan = (index) => {
    const pattern = [6, 6, 4, 4, 4];
    return pattern[index % 5];
  };

  const stats = [
    { icon: Home, value: get("stat_1_value", "150+"), label: get("stat_1_label", "Properties Sold") },
    { icon: Users, value: get("stat_2_value", "500+"), label: get("stat_2_label", "Happy Clients") },
    { icon: Award, value: get("stat_3_value", "15+"), label: get("stat_3_label", "Years Experience") },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Navbar removed - use Navigation module in CMS instead */}

      {/* No gap between navbar and hero */}

      {/* Hero Loading Placeholder */}
      {!heroReady && (
        <div 
          className="hero-section flex items-center justify-center"
          style={{ 
            backgroundColor: styles.navBgHex || '#f5f2ed',
            minHeight: `${styles.heroHeight || 80}vh`
          }}
        >
          <div className="animate-pulse text-center">
            <div className="w-16 h-16 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Loading...</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section 
        className="hero-section"
        data-testid="hero-section"
        style={{ 
          opacity: heroReady ? 1 : 0,
          visibility: heroReady ? 'visible' : 'hidden',
          transition: 'opacity 0.5s ease-in-out',
          position: heroReady ? 'relative' : 'absolute',
          pointerEvents: heroReady ? 'auto' : 'none',
          minHeight: `${styles.heroHeight || 80}vh`
        }}
      >
        <div className="absolute inset-0">
          <ProgressiveImg
            src={styles.heroImage || DEFAULT_HERO_IMAGE}
            srcSet={heroImageUrl?.includes('unsplash.com') ? 
              `${heroImageUrl}${heroImageUrl.includes('?') ? '&' : '?'}w=640&q=75 640w, ${heroImageUrl}${heroImageUrl.includes('?') ? '&' : '?'}w=1024&q=75 1024w, ${heroImageUrl}${heroImageUrl.includes('?') ? '&' : '?'}w=1920&q=75 1920w` : 
              undefined}
            sizes="100vw"
            alt="Luxury Home"
            loading="eager"
            className="w-full h-full"
            style={{ opacity: (styles.heroImageOpacity || 100) / 100 }}
            onLoad={() => setImageLoaded(true)}
          />
          {/* Text overlay background */}
          <div 
            className="absolute inset-0"
            style={{ 
              backgroundColor: styles.heroTextOverlayColor || "#000000",
              opacity: (styles.heroTextOverlayOpacity || 0) / 100 
            }}
          />
        </div>

        <div 
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full"
          style={{ textAlign: styles.heroTextAlign || 'left' }}
        >
          <div 
            className={`space-y-6 animate-fade-in-up ${
              styles.heroTextAlign === 'center' ? 'mx-auto max-w-3xl' : 'max-w-2xl'
            }`}
          >
            <h1 
              className="font-serif font-medium leading-tight drop-shadow-lg"
              style={{ 
                color: styles.heroFontHex || "#ffffff",
                fontSize: `${styles.heroTitleSize || 48}px`
              }}
            >
              {styles.heroTitle || get("hero_title", "Find Your Dream Home")}
            </h1>
            <span 
              className="inline-block tracking-widest uppercase font-semibold drop-shadow"
              style={{ 
                color: styles.heroFontHex || "#ffffff", 
                opacity: 0.8,
                fontSize: `${styles.heroSubtitleSize || 14}px`
              }}
            >
              {styles.heroSubtitle || get("hero_subtitle", "Welcome to SellLocalHomes")}
            </span>
            <p 
              className="leading-relaxed drop-shadow"
              style={{ 
                color: styles.heroFontHex || "#ffffff", 
                opacity: 0.8,
                fontSize: `${styles.heroDescriptionSize || 18}px`,
                maxWidth: styles.heroTextAlign === 'center' ? '600px' : '500px',
                margin: styles.heroTextAlign === 'center' ? '0 auto' : undefined
              }}
            >
              {styles.heroDescription || get("hero_description", "Discover exceptional properties in prime locations. Your perfect home is waiting.")}
            </p>
            
            {/* Stats on Hero */}
            <div 
              className={`flex flex-wrap items-center gap-8 md:gap-12 pt-4 ${
                styles.heroTextAlign === 'center' ? 'justify-center' : ''
              }`}
            >
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                >
                  <stat.icon 
                    className="w-6 h-6 drop-shadow" 
                    style={{ color: styles.heroFontHex || "#ffffff", opacity: 0.8 }}
                  />
                  <div className="flex items-baseline gap-2">
                    <span 
                      className="font-serif text-3xl font-medium drop-shadow-lg"
                      style={{ color: styles.heroFontHex || "#ffffff" }}
                    >
                      {stat.value}
                    </span>
                    <span 
                      className="text-sm drop-shadow"
                      style={{ color: styles.heroFontHex || "#ffffff", opacity: 0.7 }}
                    >
                      {stat.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 md:py-32" data-testid="featured-properties-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="space-y-4">
              <span className="text-sm tracking-widest uppercase font-semibold text-muted-foreground">
                {get("featured_subtitle", "Our Collection")}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-medium">
                {get("featured_title", "Featured Properties")}
              </h2>
              <div className="section-divider" />
            </div>
            <Link to="/properties">
              <Button
                variant="ghost"
                className="text-primary hover:bg-primary/5 rounded-none px-0 font-medium group"
                data-testid="view-all-btn"
              >
                View All Properties
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-muted animate-pulse h-28" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {properties.map((property, index) => (
                <Link
                  key={property.property_id}
                  to={`/property/${property.property_id}`}
                  state={propertyLinkState}
                  className="block group animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`featured-property-${property.property_id}`}
                >
                  <div 
                    className="flex flex-row gap-4 p-3 md:p-4 border hover:border-primary/40 hover:shadow-md transition-all duration-300 rounded-sm"
                    style={cardStyle}
                  >
                    {/* Smaller Image with overlay - always side by side */}
                    <div className="relative w-28 h-28 md:w-56 md:h-40 overflow-hidden shrink-0">
                    {(() => {
                      const first = property.gallery?.[0];
                      const galleryUrl = first && typeof first === "object" ? first.media_url : first;
                      const src = galleryUrl || property.main_image;
                      return (
                        <img
                          src={getOptimizedImageUrl(src, 640, 75)}
                          srcSet={getResponsiveSrcSet(src)}
                          sizes="(max-width: 768px) 112px, 224px"
                          alt={property.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      );
                    })()}
                      {/* Price & Location Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      
                      {/* Agent logo - top right */}
                      {property.agent_logo && (
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-px md:p-0.5 rounded z-10">
                          <img 
                            src={resolveImageUrl(property.agent_logo)} 
                            alt="Agent" 
                            className="h-4 w-auto max-w-[40px] md:h-5 md:max-w-[50px] object-contain slh-no-img-style"
                          />
                        </div>
                      )}

                      {/* Transaction status badge - top left */}
                      {property.transaction_status && (() => {
                        const bgMap = {
                          "SSTC":        "bg-amber-500",
                          "SSTCM":       "bg-amber-600",
                          "Under Offer": "bg-purple-600",
                          "Reserved":    "bg-blue-600",
                          "Let Agreed":  "bg-teal-600",
                        };
                        const bg = bgMap[property.transaction_status];
                        if (!bg) return null;
                        return (
                          <div
                            className={`absolute top-2 left-2 z-10 ${bg} text-white uppercase tracking-wide font-semibold px-1.5 py-0.5 text-[9px] md:text-[10px] shadow`}
                            data-testid={`featured-status-${property.transaction_status.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {property.transaction_status}
                          </div>
                        );
                      })()}
                      
                      <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3">
                        <div className="text-white font-serif text-sm md:text-xl font-medium">
                          {formatPrice(property.price, property.currency)}
                        </div>
                        <div className="hidden md:flex items-center gap-1.5 text-white/90 text-sm">
                          <MapPin size={12} />
                          <span>{property.display_address}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Info on the side - always visible */}
                    <div className="flex-1 flex flex-col justify-center min-w-0">
                      <h3 className="font-serif text-base md:text-xl font-medium text-foreground mb-1 md:mb-2 group-hover:text-primary transition-colors truncate">
                        {property.title}
                      </h3>
                      
                      {/* Location - mobile only (since it's hidden on image overlay for mobile) */}
                      <div className="flex md:hidden items-center gap-1 text-muted-foreground text-xs mb-2">
                        <MapPin size={10} />
                        <span className="truncate">{property.display_address}</span>
                      </div>
                      
                      {/* Property details row (tenure removed) */}
                      <div className="flex items-center flex-wrap gap-x-2 md:gap-x-4 gap-y-1 text-muted-foreground text-xs md:text-sm mb-2 md:mb-3">
                        <div className="flex items-center gap-1">
                          <Bed size={14} />
                          <span>{property.beds} Beds</span>
                        </div>
                        <span className="text-border">•</span>
                        <div className="flex items-center gap-1">
                          <Bath size={14} />
                          <span>{property.baths} Baths</span>
                        </div>
                        {property.property_type && (
                          <span className="hidden md:inline capitalize">• {property.property_type}</span>
                        )}
                      </div>
                      
                      {/* View Details link */}
                      <span className="text-primary text-xs md:text-sm font-medium group-hover:underline">
                        View Details →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer showAdminLink={true} />

      {/* Floating CTA - Left */}
      <div className="fixed bottom-6 left-6 z-40 animate-fade-in-up">
        <Button
          onClick={() => navigate("/property-admin/login")}
          className="h-12 px-6 bg-primary text-white hover:bg-primary/90 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          data-testid="floating-list-house-btn"
        >
          <PlusCircle size={18} />
          <span className="font-medium">List Your Home</span>
        </Button>
      </div>
    </div>
  );
}